import React from 'react';
import { useHistory } from 'react-router-dom';

import styled from '@emotion/styled';

import { Button, COLORS, List, Text } from '@clutter/clean';

import { AnchorButton } from '@shared/components/bootstrap';
import { Spacer } from '@shared/components/helpers/spacer';

import { Banner } from '@portal/components/shared/banner';
import { TrackedClick } from '@portal/components/wt/tracked_click';
import { orderURL, payURL } from '@portal/config/routes';
import { Account__State, OrderStateEnum, OrderStatusEnum } from '@portal/schema';

import { LineItem } from '../../line_item';
import { AppointmentDetailsOrder } from '../full_panel';
import { MultiDayTimelineLineItem } from './multi_day_timeline';
import { OrderAddressLineItem } from './order_address_line_item';
import { OrderContactLineItem } from './order_contact_line_item';
import { OrderInventoryLineItem } from './order_inventory_line_item';

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 32px;
  margin-bottom: 16px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const HeaderButton = styled(Button)`
  padding: 4px 12px;
  font-size: 12px;
  height: auto;

  &.cancel-button {
    color: ${COLORS.toucan};
    border-color: ${COLORS.toucan};
    &:hover {
      color: ${COLORS.toucan};
      background-color: ${COLORS.flower};
    }
  }
`;

const HeaderButtons: React.FC<{
  order: NonNullable<AppointmentDetailsOrder['longDistanceMove']>['pickup'];
}> = ({ order }) => {
  const history = useHistory();

  const showCancelButton = order.state === OrderStateEnum.Approved && order.permissions.cancelable;

  if (!showCancelButton) return null;

  const cancelButtonTrackingParams = {
    pageName: 'portal:appointment_details',
    container: 'appointment_details',
    action: 'click',
    objectType: 'button',
    objectName: 'cancel_appointment',
    label: 'Cancel',
    value: orderURL(order.id, 'cancel'),
    order_id: order.id,
  };

  return (
    <ButtonGroup>
      <TrackedClick params={cancelButtonTrackingParams}>
        <HeaderButton
          kind="secondary"
          size="small"
          className="cancel-button"
          onClick={() => {
            history.push(orderURL(order.id, 'cancel'));
          }}
        >
          Cancel
        </HeaderButton>
      </TrackedClick>
    </ButtonGroup>
  );
};

export const LongDistanceMoveInfoList: React.FC<{
  order: AppointmentDetailsOrder;
}> = ({ order }) => {
  const longDistanceMove = order.longDistanceMove;

  if (!longDistanceMove) return null;

  const pickup = longDistanceMove.pickup;
  const delivery = longDistanceMove.delivery;
  const accountSuspended = delivery?.account.state === Account__State.Suspended;

  return (
    <>
      <Header>
        <Text.Title size="medium">Pickup</Text.Title>
        <HeaderButtons order={pickup} />
      </Header>

      {pickup.multiDay && pickup.state === OrderStateEnum.Approved && (
        <>
          <Banner
            text={
              'Due to the size of your order, it has been split into multiple appointments. Contact us if you need to reschedule.'
            }
          />
          <Spacer height="2rem" />
        </>
      )}
      <List borderColor={COLORS.grayBorder}>
        <MultiDayTimelineLineItem
          order={pickup}
          showServiceDescription={false}
          alwaysShowArrivalTimes={true}
          showEdit={pickup.state === OrderStateEnum.Approved && pickup.permissions.reschedulable}
        />
        <OrderAddressLineItem
          orderId={pickup.id}
          address={pickup.address}
          label="Address"
          showEdit={pickup.status === OrderStatusEnum.Scheduled || pickup.status === OrderStatusEnum.Pending}
        />
        <OrderContactLineItem order={pickup} />
      </List>

      <Header>
        <Text.Title size="medium">Delivery</Text.Title>
      </Header>

      {delivery ? (
        <>
          {delivery.multiDay && delivery.state === OrderStateEnum.Approved && (
            <>
              <Banner
                text={
                  'Due to the size of your order, it has been split into multiple appointments. Contact us if you need to reschedule.'
                }
              />
              <Spacer height="2rem" />
            </>
          )}
          {accountSuspended && (
            <>
              <AnchorButton kind="danger" block href={payURL()} className="mt-3 mb-3">
                Unfortunately, your delivery can't be scheduled until all unpaid invoices are paid. Click here to pay.
              </AnchorButton>
              <Spacer height="2rem" />
            </>
          )}
          <List borderColor={COLORS.grayBorder}>
            <MultiDayTimelineLineItem
              order={delivery}
              showServiceDescription={false}
              alwaysShowArrivalTimes={true}
              showEdit={
                (delivery.state === OrderStateEnum.Approved || delivery.state === OrderStateEnum.Pending) &&
                delivery.permissions.reschedulable
              }
              showNotScheduled={delivery.state === OrderStateEnum.Pending}
            />
            <OrderAddressLineItem
              orderId={delivery.id}
              address={delivery.address}
              label="Address"
              showEdit={delivery.status === OrderStatusEnum.Scheduled || delivery.status === OrderStatusEnum.Pending}
            />
            <OrderContactLineItem order={delivery} />
            <OrderInventoryLineItem order={delivery} pickupCompleted={pickup.status === OrderStatusEnum.Completed} />
          </List>
        </>
      ) : (
        <Text.Body>Not Scheduled</Text.Body>
      )}

      <Header>
        <Text.Title size="medium">Pricing</Text.Title>
      </Header>

      <List borderColor={COLORS.grayBorder}>
        <LineItem label={'Flat Rate'}>{longDistanceMove.flatRateFormatted}</LineItem>
      </List>
    </>
  );
};
